import React, {useEffect, useState} from "react";
import {supabase} from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";


// Starts the admin page code. Uses AuthContext to check if user is admin  
function downloadBlob(content, filename, contentType) {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function AdminUserManagement() {
    const { user, isAdmin } = useAuth();
    const [users, setUsers] = useState([]); // holds list of user records fetched from the databases and re-renders the UI when users changes
    const [search, setSearch] = useState(""); // stores the value that the admin wants to search for
    const [loading, setLoading] = useState(true); // icon to show that the data is loading
    const [editingUser, setEditingUser] = useState(null); // shows the user what person's data they are currently editing
    const dietaryOptions = [
        { value: "none", label: "None" },
        { value: "vegetarian", label: "Vegetarian" },
        { value: "vegan", label: "Vegan" },
        { value: "halal", label: "Halal" },
        { value: "kosher", label: "Kosher" },
    ];

    const deriveDietaryPreference = (profile = {}) => {
        if (profile.dietary_preference) return profile.dietary_preference;
        if (profile.halal) return "halal";
        if (profile.vegetarian) return "vegetarian";
        return "none";
    };


    // Asks the database for uesers, saves them into "users" and shows loading while we wait
    async function fetchUsers(){
        setLoading(true);
        const { data, error } = await supabase.from("users").select("*"); //calls Supabase to select all columns from the users table
        if (error) return;
        setUsers(data || []);
        setLoading(false); // finish showing the loading icon
    }

    // When the page opens and user is admin, this function loads the users
    useEffect(() => {
        if (isAdmin && user) {
            fetchUsers();
        }
    }, [isAdmin, user]);

    if (!user) {
        return <div className="p-6 text-red-500">Please log in first</div>;
    }

    if (!isAdmin) {
        return <div className="p-6 text-red-500">Access denied - admin only</div>;
    }

    // runs when the edit form is submitted
    async function handleUpdateUser(e){
        e.preventDefault();
        
        if (!editingUser || !editingUser.id) {
            alert("Error: No user selected for editing");
            return;
        }
        
        // Extract only the fields that should be updated (exclude system fields)
        // Convert empty strings to null for optional fields
        const dietaryPreference = deriveDietaryPreference(editingUser);

        const updateData = {
            name: editingUser.name?.trim() || '',
            email: editingUser.email?.trim() || '',
            phone: editingUser.phone?.trim() || null,
            company: editingUser.company?.trim() || null,
            job_title: editingUser.job_title?.trim() || null,
            role: editingUser.role || 'attendee',
            registered: Boolean(editingUser.registered),
            paid: Boolean(editingUser.paid),
            public_profile: Boolean(editingUser.public_profile),
            ticket_number: editingUser.ticket_number?.trim() || null,
            bio: editingUser.bio?.trim() || null,
            links: editingUser.links?.trim() || null,
            clubs: Array.isArray(editingUser.clubs) ? editingUser.clubs : [],
            tech_interests: Array.isArray(editingUser.tech_interests) ? editingUser.tech_interests : [],
            dietary_preference: dietaryPreference
        };
        
        // Validate required fields
        if (!updateData.name || !updateData.email) {
            alert("Name and Email are required fields");
            return;
        }
        
        try {
            const { error } = await supabase
                .from("users")
                .update(updateData)
                .eq("id", editingUser.id)
                .select();
                
            if (error) {
                alert(`Error updating user: ${error.message}\n\nCode: ${error.code}\nDetails: ${error.details || 'None'}`);
            } else {
                alert("User updated successfully!");
                setEditingUser(null);
                fetchUsers();
            }
        } catch (err) {
            alert(`Unexpected error: ${err.message}`);
        }
    }

    async function handleDeleteUser(id) {
        if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) return;
        
        const { error } = await supabase
            .from("users")
            .delete()
            .eq("id", id);
            
        if (error) {
            alert(`Error deleting user: ${error.message}`);
        } else {
            alert("User deleted successfully!");
            fetchUsers();
        }
    }

    function exportToCSV() {
        if (users.length === 0) return;
        const headers = Object.keys(users[0]).join(",");
        const rows = users.map((u) => Object.values(u).map(value => {
            if (value === null || value === undefined) return "";
            if (Array.isArray(value)) return `"${value.join("; ")}"`;
            const sanitized = String(value).replace(/"/g, '""');
            return `"${sanitized}"`;
        }).join(",")).join("\n");
        const csvContent = headers + "\n" + rows;
        downloadBlob(csvContent, "users.csv", "text/csv;charset=utf-8;");
    }

    const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="max-w-6xl mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6"> Admin: User Management</h1>

        <div className="flex justify-between items-center mb-4">
            <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-md p-2 w-1/2"
            />
            <button
            onClick={exportToCSV}
            className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600"
            >
            Export CSV
            </button>
        </div>

        {loading ? (
            <p>Loading users...</p>
        ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 bg-white rounded-lg shadow-sm">
                <thead className="bg-gray-100">
                <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 sticky left-0 bg-gray-100 z-10">ID</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Email</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Phone</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">School / Affiliation</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Job Title</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Role</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Registered</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Paid</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Public Profile</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Ticket Number</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Bio</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Links</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Clubs</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Tech Interests</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Dietary Preference</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Created At</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Updated At</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 sticky right-0 bg-gray-100 z-10">Actions</th>
                </tr>
                </thead>
                <tbody>
                {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs font-mono text-gray-600 sticky left-0 bg-white">{user.id?.substring(0, 8)}...</td>
                    <td className="px-3 py-2 text-sm">{user.name || "-"}</td>
                    <td className="px-3 py-2 text-sm">{user.email || "-"}</td>
                    <td className="px-3 py-2 text-sm">{user.phone || "-"}</td>
                    <td className="px-3 py-2 text-sm">{user.company || "-"}</td>
                    <td className="px-3 py-2 text-sm">{user.job_title || "-"}</td>
                    <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                            user.role === 'attendee' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {user.role || "-"}
                        </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                        {user.registered ? (
                            <span className="text-green-600">✓</span>
                        ) : (
                            <span className="text-gray-400">✗</span>
                        )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                        {user.paid ? (
                            <span className="text-green-600">✓</span>
                        ) : (
                            <span className="text-gray-400">✗</span>
                        )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                        {user.public_profile ? (
                            <span className="text-green-600">✓</span>
                        ) : (
                            <span className="text-gray-400">✗</span>
                        )}
                    </td>
                    <td className="px-3 py-2 text-sm font-mono">{user.ticket_number || "-"}</td>
                    <td className="px-3 py-2 text-sm max-w-xs truncate" title={user.bio}>{user.bio || "-"}</td>
                    <td className="px-3 py-2 text-sm max-w-xs truncate" title={user.links}>{user.links || "-"}</td>
                    <td className="px-3 py-2 text-sm">
                        {user.clubs && Array.isArray(user.clubs) ? (
                            <span className="text-xs">{user.clubs.join(", ")}</span>
                        ) : user.clubs ? (
                            <span className="text-xs">{String(user.clubs)}</span>
                        ) : (
                            "-"
                        )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                        {user.tech_interests && Array.isArray(user.tech_interests) ? (
                            <span className="text-xs">{user.tech_interests.join(", ")}</span>
                        ) : user.tech_interests ? (
                            <span className="text-xs">{String(user.tech_interests)}</span>
                        ) : (
                            "-"
                        )}
                    </td>
                    <td className="px-3 py-2 text-sm">
                        {(() => {
                            const pref = deriveDietaryPreference(user);
                            const option = dietaryOptions.find(opt => opt.value === pref);
                            return option ? option.label : "-";
                        })()}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                        {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2 sticky right-0 bg-white z-10">
                        <button
                        onClick={() => setEditingUser({ ...user, dietary_preference: deriveDietaryPreference(user) })}
                        className="text-blue-500 hover:underline text-xs"
                        >
                        Edit
                        </button>
                        <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-500 hover:underline text-xs"
                        >
                        Delete
                        </button>
                    </td>
                    </tr>
                ))}
                </tbody>
            </table>
            </div>
        )}

        {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-semibold mb-4">Edit User</h2>
                <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Name</label>
                        <input
                        required
                        placeholder="Name"
                        value={editingUser.name || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, name: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Email</label>
                        <input
                        required
                        type="email"
                        placeholder="Email"
                        value={editingUser.email || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, email: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Phone</label>
                        <input
                        placeholder="Phone"
                        value={editingUser.phone || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, phone: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div>
                    <label className="block text-sm font-medium mb-1">School / Affiliation</label>
                    <input
                    placeholder="School / Affiliation"
                        value={editingUser.company || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, company: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Job Title</label>
                        <input
                        placeholder="Job Title"
                        value={editingUser.job_title || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, job_title: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Role</label>
                        <select
                        value={editingUser.role || "attendee"}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, role: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        >
                            <option value="attendee">Attendee</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Ticket Number</label>
                        <input
                        placeholder="Ticket Number"
                        value={editingUser.ticket_number || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, ticket_number: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div className="flex items-center space-x-4">
                        <label className="flex items-center">
                            <input
                            type="checkbox"
                            checked={editingUser.registered || false}
                            onChange={(e) =>
                                setEditingUser({ ...editingUser, registered: e.target.checked })
                            }
                            className="mr-2"
                            />
                            <span className="text-sm">Registered</span>
                        </label>
                        <label className="flex items-center">
                            <input
                            type="checkbox"
                            checked={editingUser.paid || false}
                            onChange={(e) =>
                                setEditingUser({ ...editingUser, paid: e.target.checked })
                            }
                            className="mr-2"
                            />
                            <span className="text-sm">Paid</span>
                        </label>
                        <label className="flex items-center">
                            <input
                            type="checkbox"
                            checked={editingUser.public_profile || false}
                            onChange={(e) =>
                                setEditingUser({ ...editingUser, public_profile: e.target.checked })
                            }
                            className="mr-2"
                            />
                            <span className="text-sm">Public Profile</span>
                        </label>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Dietary Preference</label>
                        <select
                        value={deriveDietaryPreference(editingUser)}
                        onChange={(e) => {
                            const preference = e.target.value;
                            setEditingUser({ 
                                ...editingUser, 
                                dietary_preference: preference,
                                vegetarian: false,
                                halal: false
                            });
                        }}
                        className="border p-2 w-full rounded-md"
                        >
                            {dietaryOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Bio</label>
                        <textarea
                        placeholder="Bio"
                        value={editingUser.bio || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, bio: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        rows="3"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Links</label>
                        <input
                        placeholder="Links (comma separated)"
                        value={editingUser.links || ""}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, links: e.target.value })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Clubs (comma separated)</label>
                        <input
                        placeholder="Clubs"
                        value={Array.isArray(editingUser.clubs) ? editingUser.clubs.join(", ") : (editingUser.clubs || "")}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, clubs: e.target.value.split(",").map(s => s.trim()).filter(s => s) })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Tech Interests (comma separated)</label>
                        <input
                        placeholder="Tech Interests"
                        value={Array.isArray(editingUser.tech_interests) ? editingUser.tech_interests.join(", ") : (editingUser.tech_interests || "")}
                        onChange={(e) =>
                            setEditingUser({ ...editingUser, tech_interests: e.target.value.split(",").map(s => s.trim()).filter(s => s) })
                        }
                        className="border p-2 w-full rounded-md"
                        />
                    </div>
                </div>
                <div className="flex justify-end space-x-2 pt-4 border-t">
                    <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 bg-gray-300 rounded-md"
                    >
                    Cancel
                    </button>
                    <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md"
                    >
                    Save
                    </button>
                </div>
                </form>
            </div>
            </div>
        )}
        </div>
    );
}

export default AdminUserManagement;